import { SquareArrowRightEnterIcon, TrashIcon } from 'lucide-react'

import { Button, Skeleton } from '@/components'
import {
  type Member,
  RemoveMemberDialog,
  useAccount,
  useLoadingMembers,
  useMembers,
} from '@/modules/account'
import { useUser } from '@/modules/auth'
import { useDialogActions } from '@/stores'

export const MembersList = () => {
  const members = useMembers()
  const loading = useLoadingMembers()
  const user = useUser()

  const isOwner = useAccount()?.isOwner
  const { openDialog } = useDialogActions()

  const ownerCount = members.filter(({ isOwner }) => isOwner).length
  const canRemove = (member: Member) =>
    isOwner && !(member.isOwner && ownerCount <= 1)
  const isCurrentUser = (member: Member) => user?.email === member.email

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  return (
    <>
      <div className="divide-y rounded-md border">
        {members.map((member) => (
          <div
            key={member.id}
            className="flex items-center justify-between px-4 py-3"
          >
            <div className="flex items-center gap-3">
              <span className="text-sm">{member.email}</span>
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  member.isOwner
                    ? 'bg-blue-50 text-blue-700'
                    : 'bg-neutral-100 text-neutral-600'
                }`}
              >
                {member.isOwner ? 'Owner' : 'Member'}
              </span>
            </div>
            {canRemove(member) && (
              <Button
                size="xs"
                theme="ghost"
                isIcon
                onClick={() => openDialog(RemoveMemberDialog.id, member)}
                title={isCurrentUser(member) ? 'Leave' : 'Remove'}
              >
                {isCurrentUser(member) ? (
                  <SquareArrowRightEnterIcon className="size-4" />
                ) : (
                  <TrashIcon className="size-4" />
                )}
              </Button>
            )}
          </div>
        ))}
        {members.length === 0 && (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            No team members yet.
          </p>
        )}
      </div>

      <RemoveMemberDialog />
    </>
  )
}
